/**
 * Update Overlay — global state controller.
 *
 * Tiny pub/sub used by version-check.ts (and any other producer) to drive the
 * React `UpdateOverlay` that lives at app root. Consumers subscribe via the
 * `useUpdateOverlayState` hook; producers call `setUpdateOverlayState(...)`.
 */
import { useSyncExternalStore } from 'react';

export type ChangelogItem = { icon: string; title: string; description: string };

export type UpdateOverlayState =
  | { kind: 'idle' }
  | { kind: 'updating' }
  | {
      kind: 'progress';
      percent: number;
      bytesTotal?: number;
      etaSec?: number;
      changelog?: ChangelogItem[];
      versionFrom?: string;
      versionTo?: string;
    }
  | { kind: 'minimal' }
  | { kind: 'ready'; version?: string; onContinue?: () => void };

let current: UpdateOverlayState = { kind: 'idle' };
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function setUpdateOverlayState(next: UpdateOverlayState): void {
  current = next;
  emit();
}

export function getUpdateOverlayState(): UpdateOverlayState {
  return current;
}

export function subscribeUpdateOverlay(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUpdateOverlayState(): UpdateOverlayState {
  return useSyncExternalStore(subscribeUpdateOverlay, getUpdateOverlayState, getUpdateOverlayState);
}

// Dev/debug hook on window so the overlay can be triggered without an actual
// deploy (handy for QA and screenshots). Safe in prod: just exposes a no-op
// surface — production won't call this from anywhere.
if (typeof window !== 'undefined') {
  (window as any).__updateOverlay = {
    set: setUpdateOverlayState,
    get: getUpdateOverlayState,
  };
}
